import { readMarketingReportSource, type MarketingSourceCoverage } from './marketingReportCoverage';
import { marketingLoanOfficerSocialData } from './marketingLoanOfficerSocial';
import { loadMarketingReels } from './marketingReels';
import { isSocialObservation } from './marketingSocialProfiles';
import { assertMarketingWritable } from './marketingReadOnlyScope';
import { Upload } from 'tus-js-client';
import { supabase } from '@/utils/supabase';
import type { MarketingResearchPayload } from '@/services/marketingResearchIngest';
import type { BriefCheckResult, BriefPass, BriefPreview, BriefUseId } from '../../supabase/functions/_shared/marketing-brief/types';
import type { BriefUseStatus } from '../../supabase/functions/_shared/marketing-brief/readiness';
import type {
  MarketingBrief,
  MarketingBriefConcept,
  MarketingCaptionInput,
  MarketingCaptionRequest,
  MarketingCaptionRevision,
  MarketingAiModelKey,
  MarketingConceptShell,
  MarketingConceptShellBody,
  MarketingContentReport,
  MarketingContentWeek,
  MarketingDraft,
  MarketingEvent,
  MarketingIdea,
  MarketingJob,
  MarketingMetricSnapshot,
  MarketingParticipant,
  MarketingPattern,
  MarketingProviderRun,
  MarketingPublication,
  MarketingResearch,
  MarketingResearchCheckDecision,
  MarketingResearchCheckRun,
  MarketingResearchRevision,
  MarketingResearchRevisionSummary,
  MarketingResearchWeek,
  MarketingReview,
  MarketingScriptEditRequest,
  MarketingStudioAccess,
  MarketingStudioData,
  MarketingUpload,
  MarketingUploadReservation,
  MarketingVoiceKit,
  MarketingVoicePromptBlocks,
  MarketingVoiceKitRevision,
  MarketingVoiceKitSuggestion,
  MarketingWorkerQueueTask,
  MarketingWinnerAnalysis,
  MarketingWinnerSource
} from '@/types/marketingStudio';

const MEDIA_FUNCTION_NAME = 'marketing-studio-media';
const BRIEF_FUNCTION_NAME = 'marketing-brief-ingest';
const TUS_CHUNK_BYTES = 6 * 1024 * 1024;
const MAX_VIDEO_BYTES = 500 * 1024 * 1024;
const ALLOWED_VIDEO_TYPES = new Set(['video/mp4', 'video/quicktime']);
const COMPLIANCE_FUNCTION_NAME = 'marketing-compliance-screen';

interface QueryResult {
  data: unknown;
  error: { message?: string } | null;
}

export class MarketingStudioServiceError extends Error {
  status: number | null;
  code: string | null;

  constructor(message: string, status: number | null = null, code: string | null = null) {
    super(message);
    this.name = 'MarketingStudioServiceError';
    this.status = status;
    this.code = code;
  }
}

function requireData<T>(result: QueryResult, label: string): T {
  if (result.error) {
    throw new MarketingStudioServiceError(
      result.error.message || `Could not load ${label}.`,
      null,
      'DATABASE_ERROR'
    );
  }
  return result.data as T;
}

async function invokeRpc<T>(name: string, args: Record<string, unknown> = {}): Promise<T> {
  if (!['marketing_current_access', 'marketing_admin_can_read', 'marketing_preview_content_release'].includes(name)) {
    assertMarketingWritable();
  }
  const { data, error } = await supabase.rpc(name, args);
  if (error) {
    throw new MarketingStudioServiceError(error.message, null, 'DATABASE_ERROR');
  }
  return data as T;
}

async function invokeMedia<T>(body: Record<string, unknown>): Promise<T> {
  if (body.action !== 'issueAccess') {
    assertMarketingWritable();
  }
  const { data, error } = await supabase.functions.invoke(MEDIA_FUNCTION_NAME, { body });
  if (error) {
    let message = error.message || 'The media request failed.';
    let code: string | null = null;
    const context = (error as { context?: Response }).context;
    if (context) {
      try {
        const payload = await context.clone().json() as { message?: string; error?: string };
        message = payload.message || message;
        code = payload.error || null;
      } catch {
        // Keep the function client error if its response is not JSON.
      }
    }
    throw new MarketingStudioServiceError(message, context?.status ?? null, code);
  }
  return data as T;
}

export async function getMarketingStudioAccess(): Promise<MarketingStudioAccess> {
  return invokeRpc<MarketingStudioAccess>('marketing_current_access');
}

export interface MarketingBatchDecisionResult {
  contentWeekId?: string;
  requested: number;
  decided: number;
  approved?: number;
  skipped: number;
  skippedItems: Array<Record<string, unknown>>;
  greenlitCount?: number;
}

export interface MarketingWinnerDecisionResult extends MarketingBatchDecisionResult {
  /** Only newly appended decisions; excludes duplicates, unchanged and skipped IDs. */
  decidedWinnerAnalysisIds: string[];
}

export interface MarketingConceptDecisionResult extends MarketingBatchDecisionResult {
  decidedShellIds: string[];
  decidedAt?: string | null;
  decidedBy?: string | null;
}

export interface MarketingResearchIngestResult {
  status: 'stored' | 'needs_confirmation';
  needsConfirmation: boolean;
  replaced: boolean;
  researchId: string;
  contentWeekId: string | null;
  weekEnding: string;
  pass: MarketingResearchPayload['pass'];
}

export type MarketingResearchIngestHandler = (input: {
  payload: MarketingResearchPayload;
  replace?: boolean;
}) => Promise<MarketingResearchIngestResult>;

export interface MarketingReleasePreview {
  contentWeekId: string;
  releaseId?: string;
  reportId?: string;
  greenlightEventId?: string;
  canRelease: boolean;
  blockReason: string | null;
  approvedConcepts?: Array<{ shellId: string; rank: number; title: string }>;
  approvedShellIds?: string[];
  approvedCount: number;
  eligibleLoanOfficers?: Array<{
    loUserId: string;
    firstName: string | null;
    lastName: string | null;
    email: string | null;
    voiceKitRevisionId: string;
  }>;
  eligibleLoUserIds?: string[];
  eligibleCount: number;
  skippedLoanOfficers: Array<{
    loUserId: string;
    firstName?: string | null;
    lastName?: string | null;
    email?: string | null;
    reasonCodes?: string[];
    reasons: string[];
  }>;
  skippedCount?: number;
  productionItemCount: number;
  frozen: boolean;
}

export async function setMarketingStudioEnabled(
  loanOfficerUserId: string,
  enabled: boolean
): Promise<Record<string, unknown>> {
  return invokeRpc('marketing_set_studio_enabled', {
    p_lo_user_id: loanOfficerUserId,
    p_enabled: enabled
  });
}

export const ingestMarketingResearch: MarketingResearchIngestHandler = async ({
  payload,
  replace = false
}) => invokeRpc<MarketingResearchIngestResult>('marketing_ingest_research', {
  p_payload: payload,
  p_replace: replace
});

export interface MarketingBriefCheckResponse {
  preview: BriefPreview | null;
  results: BriefCheckResult[];
  readiness: Partial<Record<BriefUseId, BriefUseStatus>> | null;
  rulesVersion: string;
}

export interface MarketingBriefSaveInput {
  fileName: string;
  content: string;
  /** One key per file chosen; a retry of the same save reuses it. */
  idempotencyKey: string;
  /** The current version the director reviewed, or null when they expect none. */
  expectedRevisionId: string | null;
  /** For a week saved outside Marketing Brief: the row fingerprint they reviewed. */
  expectedCoreFingerprint?: string | null;
  replace: boolean;
}

export interface MarketingBriefSaveResult {
  status: 'stored' | 'needs_confirmation' | 'conflict' | 'blocked';
  needsConfirmation: boolean;
  replaced?: boolean;
  replayed?: boolean;
  researchId?: string | null;
  revisionId?: string | null;
  checkRunId?: string | null;
  contentWeekId?: string | null;
  weekEnding?: string;
  pass?: BriefPass;
  currentRevisionId?: string | null;
  coreFingerprint?: string | null;
  drifted?: boolean;
  results: BriefCheckResult[];
}

export interface MarketingBriefApi {
  check(input: { fileName: string; content: string }): Promise<MarketingBriefCheckResponse>;
  save(input: MarketingBriefSaveInput): Promise<MarketingBriefSaveResult>;
  recheck(revisionId: string): Promise<{ checkRunId: string }>;
  decide(input: {
    checkRunId: string;
    checkId: string;
    decision: 'accepted' | 'withdrawn';
    note?: string | null;
  }): Promise<MarketingResearchCheckDecision>;
  fetchRevision(revisionId: string): Promise<MarketingResearchRevision>;
  fetchHistory(researchId: string): Promise<MarketingResearchRevisionSummary[]>;
  fetchCheckRun(revisionId: string): Promise<MarketingResearchCheckRun | null>;
  /** The week's rows when they fall outside the loaded window. */
  fetchWeekRows(weekOf: string): Promise<MarketingResearchWeek[]>;
}

/** Calls marketing-brief-ingest. A 409 conflict or 422 block returns its result; other failures throw. */
async function invokeBrief(body: Record<string, unknown>): Promise<{ status: number; result: Record<string, unknown> }> {
  assertMarketingWritable();
  const { data, error } = await supabase.functions.invoke(BRIEF_FUNCTION_NAME, { body });
  if (!error) {
    return { status: 200, result: ((data as { result?: Record<string, unknown> } | null)?.result) ?? {} };
  }
  const context = (error as { context?: Response }).context;
  let payload: { message?: string; error?: string; result?: Record<string, unknown> } | null = null;
  if (context) {
    try {
      payload = await context.clone().json();
    } catch {
      // Keep the function client error if its response is not JSON.
    }
  }
  const status = context?.status ?? 0;
  if (payload?.result && (status === 409 || status === 422)) {
    return { status, result: payload.result };
  }
  throw new MarketingStudioServiceError(payload?.message || error.message || 'The Marketing Brief request failed.', status || null, payload?.error ?? null);
}

const REVISION_SUMMARY_COLUMNS = 'id,research_id,week_of,research_pass,revision_no,source_kind,source_filename,source_sha256,actor_kind,created_by,created_at';

export const marketingBriefApi: MarketingBriefApi = {
  async check(input) {
    const { result } = await invokeBrief({ mode: 'check', ...input });
    return result as unknown as MarketingBriefCheckResponse;
  },
  async save(input) {
    const { status, result } = await invokeBrief({
      mode: 'save',
      fileName: input.fileName,
      content: input.content,
      idempotencyKey: input.idempotencyKey,
      expectedRevisionId: input.expectedRevisionId,
      ...(input.expectedCoreFingerprint ? { expectedCoreFingerprint: input.expectedCoreFingerprint } : {}),
      replace: input.replace
    });
    const results = Array.isArray(result.results) ? result.results as BriefCheckResult[] : [];
    if (status === 422) {
      return { status: 'blocked', needsConfirmation: false, results };
    }
    const saved = result as unknown as MarketingBriefSaveResult;
    return { ...saved, needsConfirmation: saved.status === 'needs_confirmation', results };
  },
  async recheck(revisionId) {
    const { result } = await invokeBrief({ mode: 'recheck', revisionId });
    return result as unknown as { checkRunId: string };
  },
  async decide(input) {
    return invokeRpc<MarketingResearchCheckDecision>('marketing_decide_research_check', {
      p_check_run_id: input.checkRunId,
      p_check_id: input.checkId,
      p_decision: input.decision,
      p_note: input.note?.trim() || null
    });
  },
  async fetchRevision(revisionId) {
    const result = await supabase.from('marketing_research_revisions')
      .select(`${REVISION_SUMMARY_COLUMNS},organization_id,schema_version,appendix`)
      .eq('id', revisionId)
      .single();
    return requireData<MarketingResearchRevision>(result, 'Marketing Brief version');
  },
  async fetchHistory(researchId) {
    const result = await supabase.from('marketing_research_revisions')
      .select(REVISION_SUMMARY_COLUMNS)
      .eq('research_id', researchId)
      .order('revision_no', { ascending: false });
    return requireData<MarketingResearchRevisionSummary[]>(result, 'Marketing Brief history');
  },
  async fetchCheckRun(revisionId) {
    const result = await supabase.from('marketing_research_check_runs')
      .select('id,seq,revision_id,rules_version,evaluated_on,calendar_covered_through,results,created_at')
      .eq('revision_id', revisionId)
      .order('seq', { ascending: false })
      .limit(1)
      .maybeSingle();
    return requireData<MarketingResearchCheckRun | null>(result, 'Marketing Brief checks');
  },
  async fetchWeekRows(weekOf) {
    const result = await supabase.from('marketing_research_weeks').select('*').eq('week_of', weekOf);
    return requireData<MarketingResearchWeek[]>(result, 'Marketing Brief week');
  }
};

export async function addMarketingTrackedAccount(input: {
  handle: string;
  platform: 'instagram' | 'tiktok' | 'youtube_shorts';
  topic: string;
  notes?: string;
}): Promise<string> {
  return invokeRpc<string>('marketing_add_tracked_account', {
    p_handle: input.handle,
    p_platform: input.platform,
    p_topic: input.topic,
    p_notes: input.notes?.trim() || null
  });
}

export async function setMarketingTrackedAccountState(input: {
  accountId: string;
  state: 'tracking' | 'paused' | 'removed';
  reason?: string;
}): Promise<Record<string, unknown>> {
  return invokeRpc('marketing_set_tracked_account_state', {
    p_account_id: input.accountId,
    p_state: input.state,
    p_reason: input.reason?.trim() || null
  });
}

export async function decideMarketingWinnerVideos(input: {
  winnerAnalysisIds: string[];
  decision: 'greenlit' | 'passed' | 'retracted';
  passReason?: string;
}): Promise<MarketingWinnerDecisionResult> {
  return invokeRpc<MarketingWinnerDecisionResult>('marketing_decide_winner_video', {
    p_winner_analysis_ids: input.winnerAnalysisIds,
    p_decision: input.decision,
    p_pass_reason: input.decision === 'passed' ? input.passReason?.trim() || null : null
  });
}

export async function recordMarketingGreenlightEvent(input: {
  contentWeekId: string;
  action: 'pull_reserve' | 'official_approval' | 'close_short' | 'reopen';
  winnerAnalysisId?: string;
}): Promise<Record<string, unknown>> {
  return invokeRpc('marketing_record_greenlight_event', {
    p_content_week_id: input.contentWeekId,
    p_action: input.action,
    p_winner_analysis_id: input.winnerAnalysisId ?? null
  });
}

export async function decideMarketingConceptShells(input: {
  shellIds: string[];
  decision: 'pending' | 'approved' | 'killed';
  reason?: string;
}): Promise<MarketingConceptDecisionResult> {
  return invokeRpc<MarketingConceptDecisionResult>('marketing_decide_concept_shell', {
    p_shell_ids: input.shellIds,
    p_decision: input.decision,
    p_reason: input.decision === 'killed' ? input.reason?.trim() || null : null
  });
}

export async function previewMarketingContentRelease(
  contentWeekId: string
): Promise<MarketingReleasePreview> {
  return invokeRpc<MarketingReleasePreview>('marketing_preview_content_release', {
    p_content_week_id: contentWeekId
  });
}

export async function releaseMarketingContent(
  contentWeekId: string
): Promise<Record<string, unknown>> {
  return invokeRpc('marketing_release_content', { p_content_week_id: contentWeekId });
}

export async function markMarketingProductionStage(input: {
  jobId: string;
  stage: 'script' | 'film' | 'edit' | 'lo_review' | 'publish' | null;
}): Promise<Record<string, unknown>> {
  return invokeRpc('marketing_mark_production_stage', {
    p_job_id: input.jobId,
    p_stage: input.stage
  });
}

export async function setMarketingProductionPublishDate(input: {
  jobId: string;
  publishDate: string | null;
}): Promise<Record<string, unknown>> {
  return invokeRpc('marketing_set_production_publish_date', {
    p_job_id: input.jobId,
    p_publish_date: input.publishDate
  });
}

export async function decideMarketingSubmagicProject(input: {
  projectId: string;
  action: 'approve' | 'send_back';
  note?: string;
}): Promise<Record<string, unknown>> {
  return invokeRpc('marketing_decide_submagic_project', {
    p_project_id: input.projectId,
    p_action: input.action,
    p_note: input.note?.trim() || null
  });
}

export async function setMarketingVoiceKitState(
  voiceKitId: string,
  state: 'active' | 'paused' | 'archived'
): Promise<Record<string, unknown>> {
  return invokeRpc('marketing_set_voice_kit_state', {
    p_voice_kit_id: voiceKitId,
    p_state: state
  });
}

export async function setMarketingLoAiModel(
  loanOfficerUserId: string,
  modelKey: MarketingAiModelKey
): Promise<Record<string, unknown>> {
  return invokeRpc('marketing_set_lo_ai_model', {
    p_lo_user_id: loanOfficerUserId,
    p_model_key: modelKey
  });
}

async function loadSocialHistory(): Promise<NonNullable<MarketingStudioData['socialHistory']>> {
  try {
    const { data, error } = await supabase.rpc('marketing_social_history');
    if (error || !Array.isArray(data) || !data.every(isSocialObservation)) {
      return { observations: [], unavailable: true };
    }
    return { observations: data, unavailable: false };
  } catch {
    return { observations: [], unavailable: true };
  }
}

export interface MarketingStudioLoadOptions {
  adminInspection?: boolean;
  access?: MarketingStudioAccess;
  isCurrent?: () => boolean;
  /** Resolved authenticated actor; RLS remains the authorization boundary. */
  loUserId?: string;
}

/**
 * Every table remains protected by RLS. This unified loader intentionally has
 * no organization or LO filters supplied by the browser: the database derives
 * the actor, organization, and allowed row scope from the authenticated JWT.
 */
export async function loadMarketingStudioData(includeDirectorData = false, { loUserId, access, adminInspection, isCurrent = () => true }: MarketingStudioLoadOptions = {}): Promise<MarketingStudioData> {
  // Stamped before any query: data from this read can only show work queued before this moment.
  const readStartedAt = new Date().toISOString();
  const reportCoverage: Record<string, MarketingSourceCoverage> = {};
  const report = async (table: string, column = 'created_at') => {
    const result = await readMarketingReportSource(table, readStartedAt, column, isCurrent);
    reportCoverage[table] = result.coverage;
    return result;
  };
  const [
    participants,
    workerHealth,
    briefs,
    jobs,
    uploads,
    drafts,
    reviews,
    ideas,
    captionRequests,
    captionRevisions,
    voiceKits,
    voiceKitRevisions,
    voiceKitSuggestions,
    winnerSources,
    patterns,
    providerRuns,
    workerQueue,
    events,
    research,
    publications,
    metrics,
    contentWeeks,
    winnerAnalysis,
    contentReports,
    conceptShells,
    trackedAccounts,
    trackedAccountEvents,
    trackedAccountRunSnapshots,
    greenlightDecisionEvents,
    greenlightWeekEvents,
    conceptDecisionEvents,
    contentReleases,
    contentReleaseItems,
    productionStageEvents,
    productionScheduleEvents,
    submagicProjects,
    submagicProjectEvents,
    loChannels,
    scriptEditRequests,
    loStallAlerts,
    researchWeeks,
    socialHistory,
    socialReels,
    loProfile
  ] = await Promise.all([
    includeDirectorData
      ? supabase.rpc('marketing_list_participants')
      : Promise.resolve({ data: [], error: null }),
    includeDirectorData
      ? supabase.rpc('marketing_worker_health')
      : Promise.resolve({ data: null, error: null }),
    report('marketing_briefs'),
    report('marketing_jobs'),
    report('marketing_uploads'),
    report('marketing_drafts'),
    report('marketing_reviews'),
    supabase.from('marketing_ideas').select('*').order('created_at', { ascending: false }),
    supabase.from('marketing_caption_requests').select('*').order('created_at', { ascending: false }),
    supabase.from('marketing_caption_revisions').select('*').order('created_at', { ascending: false }),
    // Voice kits are director-only; the loan-officer workspace never reads them.
    includeDirectorData
      ? supabase.from('marketing_voice_kits').select('*').order('created_at', { ascending: false })
      : Promise.resolve({ data: [], error: null }),
    includeDirectorData
      ? supabase.from('marketing_voice_kit_revisions').select('*').order('created_at', { ascending: false })
      : Promise.resolve({ data: [], error: null }),
    includeDirectorData
      ? supabase.from('marketing_voice_kit_suggestions').select('*').order('created_at', { ascending: false })
      : Promise.resolve({ data: [], error: null }),
    supabase.from('marketing_winner_sources').select('*').order('first_seen', { ascending: false }),
    supabase.from('marketing_patterns').select('*').order('created_at', { ascending: false }),
    supabase.from('marketing_provider_runs').select('*').in('lane', ['production', 'evaluation']).order('started_at', { ascending: false }).limit(1000),
    includeDirectorData
      ? supabase.from('marketing_worker_queue')
        .select('id,organization_id,task_type,payload,status,available_at,claimed_at,finished_at,created_at')
        .in('status', ['pending', 'claimed', 'error'])
        .order('created_at', { ascending: false }).limit(200)
      : Promise.resolve({ data: [], error: null }),
    // The compact view drops the uploaded brief appendix from each event, so refreshes stay small.
    report('marketing_events_compact'),
    supabase.from('marketing_research').select('*').order('week_of', { ascending: false }),
    report('marketing_publications'),
    supabase.from('marketing_metrics_snapshots').select('*').order('captured_at', { ascending: false }),
    includeDirectorData
      ? report('marketing_content_weeks')
      : Promise.resolve({ data: [], error: null }),
    includeDirectorData
      ? supabase.from('marketing_winner_analysis').select('*').order('created_at', { ascending: false })
      : Promise.resolve({ data: [], error: null }),
    includeDirectorData
      ? report('marketing_content_reports')
      : Promise.resolve({ data: [], error: null }),
    includeDirectorData
      ? report('marketing_concept_shells')
      : Promise.resolve({ data: [], error: null }),
    includeDirectorData
      ? supabase.from('marketing_tracked_accounts').select('*').order('handle', { ascending: true })
      : Promise.resolve({ data: [], error: null }),
    includeDirectorData
      ? supabase.from('marketing_tracked_account_state_events').select('*').order('created_at', { ascending: false })
      : Promise.resolve({ data: [], error: null }),
    includeDirectorData
      ? supabase.from('marketing_tracked_account_run_snapshots').select('*').order('captured_at', { ascending: false })
      : Promise.resolve({ data: [], error: null }),
    includeDirectorData
      ? supabase.from('marketing_greenlight_decision_events').select('*').order('decided_at', { ascending: false })
      : Promise.resolve({ data: [], error: null }),
    includeDirectorData
      ? supabase.from('marketing_greenlight_week_events').select('*').order('created_at', { ascending: false })
      : Promise.resolve({ data: [], error: null }),
    includeDirectorData
      ? supabase.from('marketing_concept_decision_events').select('*').order('decided_at', { ascending: false })
      : Promise.resolve({ data: [], error: null }),
    includeDirectorData
      ? report('marketing_content_releases', 'released_at')
      : Promise.resolve({ data: [], error: null }),
    includeDirectorData
      ? report('marketing_content_release_items')
      : Promise.resolve({ data: [], error: null }),
    includeDirectorData
      ? report('marketing_production_stage_events', 'marked_at')
      : Promise.resolve({ data: [], error: null }),
    includeDirectorData
      ? report('marketing_production_schedule_events')
      : Promise.resolve({ data: [], error: null }),
    includeDirectorData
      ? report('marketing_submagic_projects')
      : Promise.resolve({ data: [], error: null }),
    includeDirectorData
      ? report('marketing_submagic_project_events')
      : Promise.resolve({ data: [], error: null }),
    // RLS restricts social reads to the actor's current linked accounts.
    report('marketing_lo_channels'),
    report('marketing_script_edit_requests', 'requested_at'),
    supabase.from('marketing_lo_stall_alerts').select('id,organization_id,lo_user_id,alert_local_date,status,job_ids,sent_at,created_at').order('created_at', { ascending: false }),
    // Marketing Brief versions and checks for the latest 26 weeks (two passes a week at most); no appendix.
    includeDirectorData
      ? supabase.from('marketing_research_weeks').select('*').order('week_of', { ascending: false }).limit(52)
      : Promise.resolve({ data: [], error: null }),
    loadSocialHistory(),
    loadMarketingReels(),
    !includeDirectorData && loUserId
      ? supabase.from('users').select('id,instagram_url,tiktok_url').eq('id', loUserId).maybeSingle()
      : Promise.resolve({ data: null, error: null })
  ]);

  const loadedContentWeeks = requireData<MarketingContentWeek[]>(contentWeeks, 'content weeks');
  const loadedEvents = requireData<MarketingEvent[]>(events, 'events');
  const currentAttemptId = loadedContentWeeks.find(
    (week) => Boolean(week.current_scrape_provider_run_id)
  )?.current_scrape_provider_run_id ?? null;
  // A new run stays off the board until it finishes, so the newest Top posts
  // run can differ from the board's run; its feed is read too.
  const newestScrapeRunId = (requireData<MarketingProviderRun[]>(providerRuns, 'provider runs'))
    .find((run) => run.provider === 'apify' && run.lane === 'production')?.id ?? null;
  let currentAttemptEvents: MarketingEvent[] = [];
  if (includeDirectorData) {
    // The run contract targets about 45 scoped events. This dedicated run read
    // keeps the feed complete even if the general 100-event activity window
    // turns over during a multi-hour scrape.
    const runIds = [...new Set([currentAttemptId, newestScrapeRunId].filter((id): id is string => Boolean(id)))];
    const reads = await Promise.all(runIds.map((runId) => supabase.from('marketing_events')
      .select('*')
      .eq('actor_kind', 'worker')
      .contains('metadata', { scrapeProviderRunId: runId })
      .order('created_at', { ascending: false })));
    currentAttemptEvents = reads.flatMap((read) => requireData<MarketingEvent[]>(read, 'current Top posts run events'));
  }
  const mergedEvents = [...new Map(
    [...loadedEvents, ...currentAttemptEvents].map((event) => [event.id, event])
  ).values()].sort((left, right) => (
    Date.parse(right.created_at) - Date.parse(left.created_at)
    || right.id.localeCompare(left.id)
  ));

  if (!isCurrent()) {
    throw new Error('Marketing report scope changed. Refresh the workspace.');
  }
  if (access) {
    const current = await getMarketingStudioAccess();
    const keys = ['userId', 'organizationId', 'isDirector', 'isLoanOfficer', 'enabled', 'reason'] as const;
    if (keys.some((key) => current[key] !== access[key])
      || (adminInspection && (!current.organizationId || !(await marketingAdminCanRead(current.organizationId))))) {
      throw new Error('Marketing Studio access changed during the report read. Refresh the workspace.');
    }
  }
  const data: MarketingStudioData = {
    reportCoverage,
    readStartedAt,
    loadedAt: new Date().toISOString(),
    participants: requireData<MarketingParticipant[]>(participants, 'participants'),
    workerHealth: workerHealth.error ? null : workerHealth.data as MarketingStudioData['workerHealth'],
    workerHealthAvailable: includeDirectorData && !workerHealth.error && workerHealth.data !== null,
    briefs: requireData<MarketingBrief[]>(briefs, 'briefs'),
    jobs: requireData<MarketingJob[]>(jobs, 'jobs'),
    uploads: requireData<MarketingUpload[]>(uploads, 'uploads'),
    drafts: requireData<MarketingDraft[]>(drafts, 'drafts'),
    reviews: requireData<MarketingReview[]>(reviews, 'reviews'),
    ideas: requireData<MarketingIdea[]>(ideas, 'ideas'),
    captionRequests: requireData<MarketingCaptionRequest[]>(captionRequests, 'caption requests'),
    captionRevisions: requireData<MarketingCaptionRevision[]>(captionRevisions, 'caption revisions'),
    voiceKits: requireData<MarketingVoiceKit[]>(voiceKits, 'voice kits'),
    voiceKitRevisions: requireData<MarketingVoiceKitRevision[]>(voiceKitRevisions, 'voice-kit revisions'),
    voiceKitSuggestions: requireData<MarketingVoiceKitSuggestion[]>(voiceKitSuggestions, 'voice-kit suggestions'),
    winnerSources: requireData<MarketingWinnerSource[]>(winnerSources, 'winner sources'),
    patterns: requireData<MarketingPattern[]>(patterns, 'patterns'),
    providerRuns: requireData<MarketingProviderRun[]>(providerRuns, 'provider runs'),
    workerQueue: requireData<MarketingWorkerQueueTask[]>(workerQueue, 'worker queue'),
    events: mergedEvents,
    research: requireData<MarketingResearch[]>(research, 'research'),
    publications: requireData<MarketingPublication[]>(publications, 'publications'),
    metrics: requireData<MarketingMetricSnapshot[]>(metrics, 'metrics'),
    contentWeeks: loadedContentWeeks,
    winnerAnalysis: requireData<MarketingWinnerAnalysis[]>(winnerAnalysis, 'winner analysis'),
    contentReports: requireData<MarketingContentReport[]>(contentReports, 'content reports'),
    conceptShells: requireData<MarketingConceptShell[]>(conceptShells, 'concept shells'),
    trackedAccounts: requireData<MarketingStudioData['trackedAccounts']>(trackedAccounts, 'tracked accounts'),
    trackedAccountEvents: requireData<MarketingStudioData['trackedAccountEvents']>(trackedAccountEvents, 'tracked-account events'),
    trackedAccountRunSnapshots: requireData<MarketingStudioData['trackedAccountRunSnapshots']>(trackedAccountRunSnapshots, 'tracked-account run snapshots'),
    greenlightDecisionEvents: requireData<MarketingStudioData['greenlightDecisionEvents']>(greenlightDecisionEvents, 'greenlight decisions'),
    greenlightWeekEvents: requireData<MarketingStudioData['greenlightWeekEvents']>(greenlightWeekEvents, 'greenlight week events'),
    conceptDecisionEvents: requireData<MarketingStudioData['conceptDecisionEvents']>(conceptDecisionEvents, 'concept decisions'),
    contentReleases: requireData<MarketingStudioData['contentReleases']>(contentReleases, 'content releases'),
    contentReleaseItems: requireData<MarketingStudioData['contentReleaseItems']>(contentReleaseItems, 'content release items'),
    productionStageEvents: requireData<MarketingStudioData['productionStageEvents']>(productionStageEvents, 'production stage events'),
    productionScheduleEvents: requireData<MarketingStudioData['productionScheduleEvents']>(productionScheduleEvents, 'production schedule events'),
    submagicProjects: requireData<MarketingStudioData['submagicProjects']>(submagicProjects, 'Submagic projects'),
    submagicProjectEvents: requireData<MarketingStudioData['submagicProjectEvents']>(submagicProjectEvents, 'Submagic project events'),
    loChannels: requireData<MarketingStudioData['loChannels']>(loChannels, 'loan-officer channels'),
    socialHistory,
    socialReels,
    scriptEditRequests: requireData<MarketingScriptEditRequest[]>(scriptEditRequests, 'script edit requests'),
    loStallAlerts: requireData<MarketingStudioData['loStallAlerts']>(loStallAlerts, 'loan-officer reminders'),
    researchWeeks: requireData<MarketingResearchWeek[]>(researchWeeks, 'Marketing Brief versions')
  };
  if (includeDirectorData) {
    return data;
  }
  // The access RPC supplies the authenticated ID. RLS still owns authorization;
  // this profile read excludes retained channels after an LO changes their links.
  const profile = requireData<{ id: string; instagram_url: string | null; tiktok_url: string | null } | null>(loProfile, 'your social profile');
  return marketingLoanOfficerSocialData(data, loUserId, {
    instagram: profile?.id === loUserId ? profile?.instagram_url : null,
    tiktok: profile?.id === loUserId ? profile?.tiktok_url : null
  });
}

export async function submitMarketingIdea(ideaText: string): Promise<string> {
  return invokeRpc<string>('marketing_submit_idea', { p_idea_text: ideaText });
}

export async function respondToMarketingIdea(input: {
  ideaId: string;
  action: 'draft' | 'decline';
  note?: string;
}): Promise<Record<string, unknown>> {
  return invokeRpc('marketing_respond_to_idea', {
    p_idea_id: input.ideaId,
    p_action: input.action,
    p_note: input.note?.trim() || null
  });
}

export async function submitCaptionRequest(input: MarketingCaptionInput): Promise<string> {
  return invokeRpc<string>('marketing_submit_caption_request', { p_structured_input: input });
}

export async function acknowledgeAndGetApprovedCaption(
  captionRequestId: string
): Promise<{ copyEventId: string; captionRevisionId: string; captionText: string }> {
  return invokeRpc('marketing_acknowledge_caption_copy', {
    p_caption_request_id: captionRequestId,
    p_acknowledgement: {
      accepted: true,
      statement: 'I will publish only Brandon-approved copy and keep the required caption block intact.'
    }
  });
}

export async function saveMarketingVoiceKit(input: {
  loanOfficerUserId: string;
  promptBlocks: MarketingVoicePromptBlocks;
  importedFromDocument?: boolean;
}): Promise<Record<string, unknown>> {
  return invokeRpc('marketing_save_voice_kit', {
    p_lo_user_id: input.loanOfficerUserId,
    p_prompt_blocks: input.promptBlocks,
    p_imported_from_document: input.importedFromDocument ?? false
  });
}

export async function suggestMarketingVoiceKitCorrection(input: {
  voiceKitId: string;
  suggestion: string;
}): Promise<string> {
  return invokeRpc<string>('marketing_suggest_voice_kit_correction', {
    p_voice_kit_id: input.voiceKitId,
    p_suggestion: input.suggestion
  });
}

export async function resolveMarketingVoiceKitSuggestion(input: {
  suggestionId: string;
  action: 'accept' | 'decline';
  note?: string;
  promptBlocks?: MarketingVoicePromptBlocks;
}): Promise<Record<string, unknown>> {
  return invokeRpc('marketing_resolve_voice_kit_suggestion', {
    p_suggestion_id: input.suggestionId,
    p_action: input.action,
    p_note: input.note?.trim() || null,
    p_prompt_blocks: input.promptBlocks ?? null
  });
}

export async function saveMarketingBriefRevision(input: {
  briefId: string;
  concepts: Array<Record<string, unknown>>;
}): Promise<string> {
  return invokeRpc<string>('marketing_save_brief_revision', {
    p_brief_id: input.briefId,
    p_concepts: input.concepts
  });
}

export async function transitionMarketingBrief(
  briefId: string,
  action: 'approve' | 'send' | 'archive'
): Promise<Record<string, unknown>> {
  return invokeRpc('marketing_transition_brief', {
    p_brief_id: briefId,
    p_action: action
  });
}

export async function createMarketingJob(briefId: string, conceptIndex: number): Promise<{
  jobId: string;
  jobCode: string;
}> {
  return invokeRpc('marketing_create_job', {
    p_brief_id: briefId,
    p_concept_index: conceptIndex
  });
}

export async function enqueueMarketingDirectorTask(input: {
  taskType: 'generate_briefs' | 'weekly_research' | 'monday_delta' | 'local_model_evaluation' | 'weekly_scrape';
  payload: Record<string, unknown>;
  idempotencyKey: string;
}): Promise<string> {
  return invokeRpc<string>('marketing_director_enqueue', {
    p_task_type: input.taskType,
    p_payload: input.payload,
    p_idempotency_key: input.idempotencyKey
  });
}

/** A new key per click is required because director enqueue preserves prior rows. */
export function marketingScrapeRetryIdempotencyKey(weekEnding: string, nowMs = Date.now()): string {
  return `weekly-scrape-retry:${weekEnding}:${nowMs}`;
}

/** The admin Retry Top posts request, wherever the button lives; send it with enqueueMarketingDirectorTask. */
export function marketingTopPostsRetryTask(weekEnding: string, nowMs = Date.now()) {
  return {
    taskType: 'weekly_scrape' as const,
    payload: { weekEnding, retryRequestedAt: new Date(nowMs).toISOString() },
    idempotencyKey: marketingScrapeRetryIdempotencyKey(weekEnding, nowMs)
  };
}

/** Re-scoring the board's unscored videos rides the same admin-only weekly_scrape lane. */
export function marketingRescoreIdempotencyKey(weekEnding: string, nowMs = Date.now()): string {
  return `weekly-scrape-rescore:${weekEnding}:${nowMs}`;
}

/** The admin Re-score request for a week's unscored videos; send it with enqueueMarketingDirectorTask. */
export function marketingTopPostsRescoreTask(weekEnding: string, nowMs = Date.now()) {
  return {
    taskType: 'weekly_scrape' as const,
    payload: { weekEnding, mode: 'rescore', rescoreRequestedAt: new Date(nowMs).toISOString() },
    idempotencyKey: marketingRescoreIdempotencyKey(weekEnding, nowMs)
  };
}

export async function decideMarketingConceptShell(input: {
  shellId: string;
  decision: 'approved' | 'killed' | 'edited';
  reason?: string;
  title?: string;
  shell?: MarketingConceptShellBody;
}): Promise<Record<string, unknown> | MarketingBatchDecisionResult> {
  if (input.decision !== 'edited') {
    return decideMarketingConceptShells({
      shellIds: [input.shellId],
      decision: input.decision,
      reason: input.reason
    });
  }
  if (input.decision === 'edited') {
    if (!input.shell || !input.title?.trim()) {
      throw new MarketingStudioServiceError(
        'A title and complete structured shell are required for an edit.',
        400,
        'INVALID_SHELL_EDIT'
      );
    }
  }
  assertMarketingWritable();
  const { data, error } = await supabase.functions.invoke(COMPLIANCE_FUNCTION_NAME, {
    body: {
      action: 'decideConceptShell',
      shellId: input.shellId,
      decision: input.decision,
      ...(input.decision === 'edited'
        ? { title: input.title?.trim(), shell: input.shell }
        : {})
    }
  });
  if (error) {
    throw new MarketingStudioServiceError(
      error.message || 'The concept-shell decision failed.',
      (error as { context?: Response }).context?.status ?? null,
      'CONCEPT_SHELL_DECISION_FAILED'
    );
  }
  const payload = data as {
    success?: boolean;
    result?: { shellId: string; decision: 'approved' | 'killed'; version: number };
  } | null;
  if (!payload?.success || !payload.result) {
    throw new MarketingStudioServiceError(
      'The concept-shell decision returned an invalid result.',
      null,
      'CONCEPT_SHELL_DECISION_FAILED'
    );
  }
  return payload.result;
}

export async function decideMarketingReview(input: {
  subjectType: 'video' | 'caption';
  jobId?: string | null;
  draftId?: string | null;
  captionRequestId?: string | null;
  captionRevisionId?: string | null;
  action: 'approve' | 'kickback';
  checklist: Record<string, boolean>;
  note?: string;
}): Promise<Record<string, unknown>> {
  return invokeRpc('marketing_review_decide', {
    p_subject_type: input.subjectType,
    p_job_id: input.jobId ?? null,
    p_draft_id: input.draftId ?? null,
    p_caption_request_id: input.captionRequestId ?? null,
    p_caption_revision_id: input.captionRevisionId ?? null,
    p_action: input.action,
    p_checklist: input.checklist,
    p_note: input.note?.trim() || null
  });
}

export interface MarketingScriptEditResult {
  /** `blocked` means compliance refused the edit and nothing was sent. */
  status: 'sent' | 'blocked';
  requestId: string | null;
  compliance: MarketingScriptEditRequest['compliance'];
}

/**
 * A loan officer proposes script changes. The compliance function screens the
 * proposed concept first; only a non-blocked edit is stored for Brandon.
 */
export async function requestMarketingScriptEdit(input: {
  jobId: string;
  briefId: string;
  concept: MarketingBriefConcept;
  note?: string;
}): Promise<MarketingScriptEditResult> {
  assertMarketingWritable();
  const { data, error } = await supabase.functions.invoke(COMPLIANCE_FUNCTION_NAME, {
    body: {
      action: 'requestLoScriptEdit',
      jobId: input.jobId,
      briefId: input.briefId,
      concept: input.concept,
      note: input.note?.trim() || null
    }
  });
  if (error) {
    throw new MarketingStudioServiceError(
      error.message || 'The script edit could not be sent.',
      (error as { context?: Response }).context?.status ?? null,
      'SCRIPT_EDIT_REQUEST_FAILED'
    );
  }
  const payload = data as { success?: boolean; result?: MarketingScriptEditResult } | null;
  if (!payload?.success || !payload.result) {
    throw new MarketingStudioServiceError('The script edit returned an invalid result.', null, 'SCRIPT_EDIT_REQUEST_FAILED');
  }
  return payload.result;
}

export async function withdrawMarketingScriptEdit(requestId: string): Promise<Record<string, unknown>> {
  return invokeRpc('marketing_withdraw_script_edit', { p_request_id: requestId });
}

/** Brandon approves (a new brief version becomes the script) or keeps the original with a note. */
export async function decideMarketingScriptEdit(input: {
  requestId: string;
  action: 'approve' | 'decline';
  note?: string;
}): Promise<Record<string, unknown>> {
  return invokeRpc('marketing_decide_script_edit', {
    p_request_id: input.requestId,
    p_action: input.action,
    p_note: input.note?.trim() || null
  });
}

/**
 * The loan officer's "Submit for editing": the existing verified upload path,
 * attached to the released production item. Editing happens downstream.
 */
export async function submitMarketingFootage(input: {
  jobId: string;
  file: File;
  onProgress?: (progress: number) => void;
}): Promise<{ uploadId: string }> {
  await validateMarketingVideo(input.file);
  const duration = await getMarketingVideoDuration(input.file);
  const reservation = await reserveMarketingUpload(input.jobId, input.file);
  const task = createMarketingUploadTask({ file: input.file, reservation, onProgress: input.onProgress ?? (() => undefined) });
  await task.start();
  await finalizeMarketingUpload(reservation.reservation.uploadId, duration);
  return { uploadId: reservation.reservation.uploadId };
}

export async function markMarketingJobPublished(input: {
  jobId: string;
  draftId: string;
  postUrl: string;
  caption: string;
  publishedAt?: string;
}): Promise<string> {
  return invokeRpc<string>('marketing_mark_published', {
    p_job_id: input.jobId,
    p_post_url: input.postUrl,
    p_publication_snapshot: {
      draftId: input.draftId,
      caption: input.caption,
      publishedAt: input.publishedAt ?? new Date().toISOString()
    }
  });
}

export async function recordMarketingMetrics(input: {
  publicationId: string;
  plays?: number | null;
  likes?: number | null;
  comments?: number | null;
  saves?: number | null;
  shares?: number | null;
  capturedAt?: string;
}): Promise<string> {
  return invokeRpc<string>('marketing_record_metrics', {
    p_publication_id: input.publicationId,
    p_metrics: {
      plays: input.plays ?? null,
      likes: input.likes ?? null,
      comments: input.comments ?? null,
      saves: input.saves ?? null,
      shares: input.shares ?? null,
      capturedAt: input.capturedAt ?? new Date().toISOString()
    }
  });
}

async function readBlobBytes(blob: Blob): Promise<Uint8Array> {
  if (typeof blob.arrayBuffer === 'function') {
    return new Uint8Array(await blob.arrayBuffer());
  }
  return new Promise<Uint8Array>((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error || new Error('The selected video could not be read.'));
    reader.onload = () => {
      if (!(reader.result instanceof ArrayBuffer)) {
        reject(new Error('The selected video could not be read.'));
        return;
      }
      resolve(new Uint8Array(reader.result));
    };
    reader.readAsArrayBuffer(blob);
  });
}

export async function validateMarketingVideo(file: File): Promise<void> {
  if (!ALLOWED_VIDEO_TYPES.has(file.type)) {
    throw new MarketingStudioServiceError('Choose an MP4 or MOV video.', 400, 'INVALID_FILE_TYPE');
  }
  if (!Number.isSafeInteger(file.size) || file.size <= 0 || file.size > MAX_VIDEO_BYTES) {
    throw new MarketingStudioServiceError('The video must be between 1 byte and 500 MB.', 400, 'INVALID_FILE_SIZE');
  }
  const extension = file.name.toLowerCase().split('.').pop();
  if ((file.type === 'video/mp4' && extension !== 'mp4')
    || (file.type === 'video/quicktime' && extension !== 'mov')) {
    throw new MarketingStudioServiceError('The filename does not match the video type.', 400, 'INVALID_FILE_TYPE');
  }
  const prefix = await readBlobBytes(file.slice(0, 16));
  const hasFtyp = prefix.length >= 12
    && prefix[4] === 0x66
    && prefix[5] === 0x74
    && prefix[6] === 0x79
    && prefix[7] === 0x70;
  if (!hasFtyp) {
    throw new MarketingStudioServiceError('The selected file is not a valid MP4 or MOV container.', 400, 'INVALID_VIDEO');
  }
}

export async function getMarketingVideoDuration(file: File): Promise<number> {
  return new Promise<number>((resolve, reject) => {
    const video = document.createElement('video');
    const url = URL.createObjectURL(file);
    const cleanup = () => {
      URL.revokeObjectURL(url);
      video.removeAttribute('src');
    };
    video.preload = 'metadata';
    video.onloadedmetadata = () => {
      const duration = video.duration;
      cleanup();
      if (!Number.isFinite(duration) || duration <= 0 || duration > 900) {
        reject(new MarketingStudioServiceError('The video must be 15 minutes or shorter.', 400, 'INVALID_DURATION'));
        return;
      }
      resolve(duration);
    };
    video.onerror = () => {
      cleanup();
      reject(new MarketingStudioServiceError('The browser could not read the video duration.', 400, 'INVALID_VIDEO'));
    };
    video.src = url;
  });
}

export async function reserveMarketingUpload(
  jobId: string,
  file: File
): Promise<MarketingUploadReservation> {
  return invokeMedia<MarketingUploadReservation>({
    action: 'reserveUpload',
    jobId,
    originalName: file.name,
    sizeBytes: file.size,
    mimeType: file.type
  });
}

export interface MarketingUploadTask {
  start: () => Promise<void>;
  abort: () => Promise<void>;
}

export function createMarketingUploadTask(input: {
  file: File;
  reservation: MarketingUploadReservation;
  onProgress: (percentage: number) => void;
}): MarketingUploadTask {
  assertMarketingWritable();
  const publishableKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
  if (!publishableKey) {
    throw new MarketingStudioServiceError('The Storage publishable key is not configured.', 500, 'CLIENT_CONFIGURATION_ERROR');
  }
  let settle: { resolve: () => void; reject: (error: Error) => void } | null = null;
  const upload = new Upload(input.file, {
    endpoint: input.reservation.upload.endpoint,
    headers: {
      apikey: publishableKey,
      'x-signature': input.reservation.upload.token
    },
    metadata: {
      bucketName: input.reservation.upload.bucket,
      objectName: input.reservation.upload.objectPath,
      contentType: input.file.type,
      cacheControl: '3600'
    },
    fingerprint: async (file) => [
      'marketing-video',
      input.reservation.upload.objectPath,
      file.name,
      file.size,
      file.lastModified
    ].join('::'),
    uploadDataDuringCreation: true,
    chunkSize: TUS_CHUNK_BYTES,
    retryDelays: [0, 3000, 5000, 10000, 20000],
    removeFingerprintOnSuccess: true,
    onProgress: (uploaded, total) => {
      input.onProgress(total > 0 ? Math.round((uploaded / total) * 100) : 0);
    },
    onError: (error) => {
      settle?.reject(error);
      settle = null;
    },
    onSuccess: () => {
      input.onProgress(100);
      settle?.resolve();
      settle = null;
    }
  });

  return {
    start: async () => {
      if (settle) {
        throw new MarketingStudioServiceError('This upload is already running.', 409, 'UPLOAD_RUNNING');
      }
      await new Promise<void>((resolve, reject) => {
        settle = { resolve, reject };
        upload.findPreviousUploads().then((previous) => {
          if (previous.length > 0) {
            upload.resumeFromPreviousUpload(previous[0]);
          }
          upload.start();
        }).catch((error: Error) => {
          settle = null;
          reject(error);
        });
      });
    },
    abort: async () => {
      const pending = settle;
      settle = null;
      await upload.abort(false);
      pending?.reject(new MarketingStudioServiceError('Upload cancelled.', 499, 'UPLOAD_CANCELLED'));
    }
  };
}

export async function finalizeMarketingUpload(uploadId: string, durationSeconds: number): Promise<void> {
  await invokeMedia({ action: 'finalizeUpload', uploadId, durationSeconds });
}

export async function issueMarketingMediaAccess(input: {
  draftId?: string;
  uploadId?: string;
}): Promise<{ url: string; expiresIn: number }> {
  return invokeMedia({ action: 'issueAccess', ...input });
}

/** Save the two existing employee profile URLs; the database queues eligible links. */
export async function saveMarketingSocialProfiles(input: {
  loanOfficerId: string; instagramUrl: string | null; tiktokUrl: string | null;
  expectedInstagramUrl: string | null; expectedTiktokUrl: string | null;
}): Promise<{ instagramUrl: string | null; tiktokUrl: string | null; eligible: boolean; queueId: string | null }> {
  return invokeRpc('marketing_save_social_profiles', {
    p_lo_user_id: input.loanOfficerId, p_instagram_url: input.instagramUrl, p_tiktok_url: input.tiktokUrl,
    p_expected_instagram_url: input.expectedInstagramUrl, p_expected_tiktok_url: input.expectedTiktokUrl
  });
}

export async function marketingAdminCanRead(organizationId: string): Promise<boolean> {
  return invokeRpc<boolean>('marketing_admin_can_read', { p_organization_id: organizationId });
}

/** Four bounded reads for the idle Overview. Other Studio sections are snapshots
 * refreshed explicitly; they must not download the entire director graph each minute. */
export async function loadMarketingSocialData(): Promise<Pick<MarketingStudioData,
  'loChannels' | 'socialHistory' | 'socialReels' | 'workerHealth' | 'workerHealthAvailable' | 'socialLoadedAt' | 'reportCoverage'>> {
  const cutoff = new Date().toISOString();
  const [channels, socialHistory, socialReels, health] = await Promise.all([
    supabase.from('marketing_lo_channels').select('*', { count: 'exact' }).order('handle', { ascending: true }).limit(1001),
    loadSocialHistory(), loadMarketingReels(), supabase.rpc('marketing_worker_health')
  ]);
  const rows = requireData<MarketingStudioData['loChannels']>(channels, 'social profiles');
  const complete = channels.count !== null && channels.count !== undefined && channels.count <= rows.length && rows.length <= 1000;
  return { loChannels: rows.slice(0, 1000),
    reportCoverage: { marketing_lo_channels: { from: null, to: cutoff, observedAt: new Date().toISOString(),
      status: complete ? 'complete' : 'limited', reason: complete ? 'All current visible accounts were read.' : 'The lightweight refresh has limited account coverage. Refresh activity for the full report.' } },
    socialHistory, socialReels, workerHealth: health.error ? null : health.data,
    workerHealthAvailable: !health.error && health.data !== null, socialLoadedAt: new Date().toISOString() };
}
